/**
 * The evidence "Ask Iris" sends as `canvas_state` (CU-869fkdhjv phase 3, plan § 2.1).
 *
 * The workspace's envelope (`view` / `params` / `digest`, freshness inside `digest`:
 * `rentacar-ia-workspace/src/views/Positioning/index.jsx:155-230`) plus `selection`, the clicked element.
 * Keys go out in the order `view, screen, selection, digest, params`: the brain cuts `canvas_state` silently at
 * ~2000 characters from the end (brain `server.py:1037`), so the selection and the digest are the last to go.
 * We never rely on that cut: `fitBudget` keeps the whole object under 1900 characters of compact JSON and says
 * so (`digest.truncated`) when it had to drop something, so Iris does not affirm what it did not receive
 * (criterion 9).
 *
 * `params` is `{ scope, labels, ids }`: `scope` (office, currency, Min/Max…) is never cut; `labels` (label maps)
 * go first, then the digest's lists from their tail, then `ids`.
 *
 * Without DS Repricing (criterion 26) nothing of the engine travels — no decision, pass, cap, strategy — wherever
 * a builder put it, `params` included: `stripRepricing` is the last word.
 *
 * Pure and alias-free (TL R4).
 */
export const ASK_BUDGET = 1900

/**
 * One line of "how it is calculated" per element, from the help pages (`company/product/help/analyzers-*.md`,
 * `ds-repricing-activity.md`), so the explanation matches the help (criterion 7) even if the brain does not read
 * the help pack. What the help marks "pendiente de confirmar" is not stated here.
 */
export const DEFINITIONS = {
	cell: 'Price = lowest (Min tab) or highest (Max tab) offer of that row and pick-up date under the filters, rounded to 2 decimals, total or per day as the table shows. Change % = (current / previous - 1) x 100 against the previous execution of the same report. "Vs" % = (row price / reference price - 1) x 100, absolute. Green = cheapest of the column, red = dearest.',
	cellDecisions: 'As a price cell, plus the repricing engine decisions of the open pass on this cell: the price it decided, the limit that acted and what was sent to the PMS.',
	rankCell: 'Price = cheapest offer of that brand on that pick-up date. Gap = this brand price minus the reference brand price that day. Rank 1 = cheapest of the brands quoting that date.',
	chart: 'One line per site or provider with the table price (Min or Max) along the axis; "Variation (%)" = change of each series against the previous execution. Only the series the user has visible are listed.',
	evolution: 'Evolution Chart: x axis = capture day (the day the price was collected, the 30 days before this execution), not the pick-up date; y axis = the Min (or Max) price, one line per site, for the same pick-up date and length.',
	reading: 'Built from the Price index and the Avg market rank. "Still undercut by" lists rivals cheaper than the brand on at least half of the compared dates. "Price leader" when the index is 100 or less.',
	'kpi.avgMarketRank': 'Average of the brand rank on each pick-up date (1 = cheapest); "cheapest on X% of dates" = share of dates where it was number 1.',
	'kpi.priceIndex': 'Price index = 100 x (average of the brand prices over all dates) / (average of the competitors daily average prices, brand excluded).',
	'kpi.top3Share': 'Top-3 share = dates with the brand ranked 1 to 3 / dates analysed x 100.',
	'kpi.gapCheapest': 'Gap to cheapest = average, over dates with a price of the brand and of a rival, of (brand price - cheapest rival price). In currency, not %.',
	'kpi.gapAverage': 'Gap to average = average of (brand price - rivals average price that day). In currency, not %.',
	band: 'Position over time: per pick-up date, the competitors price range (min-max), the market average and the brand price, each the cheapest offer of each brand.',
	rankChart: 'Rank evolution: market rank (1 = cheapest) of each brand per pick-up date; a break means no rate that day.',
	event: 'One engine decision: market reference, our price today, what the strategy decided, the limits that acted, the price to set in the PMS and the result. Simulated = dry run, nothing was sent to the PMS.',
	count: 'Counters of the Activity log for the period and filters shown: one decision is one event, however many pricelists it wrote.',
	// BI dashboards (wave 2): `company/product/help/business-logistics-{season-vs-last-year,price-curve-of-a-date,my-office-network}.md`
	'bi.seasonCell': 'Season vs last year cell: the provider price (Min, Avg or Max of every price captured for that pick-up, office and length) now and in the comparison period; change € = now - before, change % = change / before. Market median = median of the competitors (your brands excluded), compared only when at least 2 have both periods. Fewer than 5 prices in either period = few prices (!).',
	'bi.seasonCurve': 'Price curve of a Season cell: each point is a provider price (Min, Avg or Max) on one capture day, by days before pick-up, over the 45 days before it; Market median = median of the competitors shown that day. The last point is one capture day, so it does not equal the cell, which sums up the whole period. Hollow point = fewer than 5 prices that day.',
	'bi.dateCurve': 'Price curve of a date: each point is a provider price (Min, Avg or Max) on one capture day, by days before pick-up, over the 60 days before it; one line per provider shown; Market median = median of the competitors shown with a price that day (your brands excluded). The dotted line is last year, same weekday unless "same date". Hollow point = fewer than 5 prices that day.',
	'bi.last7': 'Last 7 days: each provider price on its last capture day vs 7 days before (or the nearest earlier capture day), in % and €; biggest drop first.',
	'bi.whoMovedFirst': 'Who moved first: a price moved when it changed 5 % or more vs its previous capture day with a price; the first such move (furthest from pick-up), and who moved 5 % or more the same way in the next 7 days.',
	'bi.position': 'Your position: rank among your main brand and the competitors with a price on the last capture day (your other brands out), 1 = the cheapest, ties share it; gap to the cheapest, or below the second when you are first; distance to Market median.',
	'bi.weeklyCell': 'Week by week cell: Min, Avg or Max of the prices captured in that capture week (Sunday to Saturday) for that office and pick-up date, and its change vs the previous selected week. L = cheapest, H = dearest of the providers shown (at least 2 with a price). ! = fewer than 5 prices.',
	'bi.networkCell': 'My office network cell: distance = (your price - median) / median, in % and €; the median is the competitors median in that office and column, your brands excluded, at least 2 competitors. Within +-2 % = in line. Change = how the distance moved since the comparison, in points. Position: 1 = the cheapest.',
	'bi.networkMarket': 'My office network without a brand: each cell is the median of every provider shown in that office and column, and its change since the comparison period, in %.',
	'bi.group': 'Group summary row: per column, the median of the distances of the group offices with data, and how many of them are above the market (+2 % or more); without a brand, the median of their market changes.',
	'bi.ranking': 'Rankings over every office and column of the view: Most above / Most below the market (distance 2 % or more), Biggest change of your distance (half a point or more, in points); without a brand, Where the market moved most (median change 1 % or more). Top 5 each.',
}

const ENGINE_TOKENS = new Set(['decision', 'decisions', 'pass', 'passes', 'cap', 'caps', 'capped', 'chain', 'engine', 'repricing', 'strategy', 'strategies'])

// `repricing_pass_id`, `passId`, `idRepricingExecution`, `cap_chain` → their words
const tokensOf = key => String(key)
	.replace(/([a-z0-9])([A-Z])/g, '$1_$2')
	.toLowerCase()
	.split(/[^a-z0-9]+/)
	.filter(Boolean)

const isEngineKey = key => {
	const tokens = tokensOf(key)
	// `cap` and `chain` only as engine words together or on their own: `capture_age` is not a cap
	return tokens.some(t => ENGINE_TOKENS.has(t) && t !== 'chain') || (tokens.includes('cap') && tokens.includes('chain'))
}

export function stripRepricing(value) {
	if (Array.isArray(value)) return value.map(stripRepricing)
	if (!value || typeof value !== 'object') return value
	const out = {}
	for (const [k, v] of Object.entries(value)) if (!isEngineKey(k)) out[k] = stripRepricing(v)
	return out
}

export function canvasState({ view, screen, selection = {}, digest = {}, params = {}, freshness = {}, definition, dsRepricing = true }) {
	const def = definition ? DEFINITIONS[definition] : undefined
	const state = {
		view,
		screen,
		selection,
		digest: { ...(def ? { definition: def } : {}), ...digest, ...freshness },
		params,
	}
	return dsRepricing ? state : stripRepricing(state)
}

const sizeOf = state => JSON.stringify(state).length

// The longest list of the digest, by entries (the ones a cut can still shorten)
function longestList(digest) {
	let best = null
	for (const [k, v] of Object.entries(digest)) {
		if (Array.isArray(v) && v.length && (!best || v.length > digest[best].length)) best = k
	}
	return best
}

/** `{ state, trimmed }`: the state under `budget` characters of `JSON.stringify`, never cutting `selection`. */
export function fitBudget(state, budget = ASK_BUDGET) {
	if (sizeOf(state) <= budget) return { state, trimmed: false }
	const out = JSON.parse(JSON.stringify(state))
	out.digest = { ...(out.digest || {}), truncated: true }
	const params = out.params || (out.params = {})
	const over = () => sizeOf(out) > budget

	// 1 · label maps, last key first
	if (params.labels) {
		for (const k of Object.keys(params.labels).reverse()) {
			if (!over()) break
			delete params.labels[k]
		}
		if (!Object.keys(params.labels).length) delete params.labels
	}
	// 2 · the digest's lists, from their tail, the longest first
	while (over()) {
		const k = longestList(out.digest)
		if (!k) break
		out.digest[k].pop()
		if (!out.digest[k].length) delete out.digest[k]
	}
	// 3 · ids beyond the scope, last key first
	if (over() && params.ids) {
		for (const k of Object.keys(params.ids).reverse()) {
			if (!over()) break
			delete params.ids[k]
		}
		if (!Object.keys(params.ids).length) delete params.ids
	}
	// 4 · the definition, which the help also has
	if (over()) delete out.digest.definition
	return { state: out, trimmed: true }
}

// `2026-10-27 09:30`, `2026-10-27T09:30`, `27/10/2026 09:30` (and without the time) → Date, or null
function parseCapture(value) {
	const text = String(value ?? '').trim()
	let m = /^(\d{4})-(\d{2})-(\d{2})(?:[ T](\d{2}):(\d{2}))?/.exec(text)
	if (m) return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]), Number(m[4] || 0), Number(m[5] || 0))
	m = /^(\d{2})\/(\d{2})\/(\d{4})(?:[ T](\d{2}):(\d{2}))?/.exec(text)
	if (m) return new Date(Number(m[3]), Number(m[2]) - 1, Number(m[1]), Number(m[4] || 0), Number(m[5] || 0))
	return null
}

/** How old the capture is, as the brain reads it ("3 h", "2 d", "< 1 h"); null when the time cannot be read. */
export function captureAge(executionDateTime, now = new Date()) {
	const at = parseCapture(executionDateTime)
	if (!at || Number.isNaN(at.getTime())) return null
	const hours = (now.getTime() - at.getTime()) / 3600000
	if (hours < 0) return null
	if (hours < 1) return '< 1 h'
	if (hours < 48) return `${Math.floor(hours)} h`
	return `${Math.floor(hours / 24)} d`
}
